import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Wrench, Cog, Disc3, ScanLine, Snowflake, CircleDot, ArrowRight, Phone, MapPin, Clock, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pathFor } from "@/i18n/translations";
import { siteConfig, openingHours } from "@/config/site";
import { IMAGES } from "@/config/images";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { AccentButton, OutlineButton, Overline } from "@/components/ui-kit";
import { BrandMarquee } from "@/components/BrandMarquee";
import { ServiceWhatsApp } from "@/components/ContactWidgets";

const QS_ICONS = [Wrench, Cog, Disc3, ScanLine, Snowflake, CircleDot];

export default function Home() {
  const { lang, t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="on-dark relative min-h-screen flex items-center overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={IMAGES.storefront} alt={t.alts.storefront} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/70 to-[#0A0A0C]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] to-transparent" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-8 pt-28 pb-16">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <Overline>{t.hero.overline}</Overline>
          </motion.div>
          <h1 className="mt-5 font-display font-semibold text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.06]">
            <MaskedLines lines={t.hero.title} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <AccentButton to={pathFor(lang, "contact")} testid="hero-book-btn">{t.cta.book}</AccentButton>
            <OutlineButton href="#quick-services" testid="hero-discover-btn">{t.cta.discover}</OutlineButton>
          </motion.div>

          {/* Contact info near hero */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-3xl"
          >
            {[
              { icon: Phone, label: t.hero.phone, value: siteConfig.phone, href: siteConfig.phoneHref },
              { icon: MapPin, label: t.hero.address, value: `${siteConfig.address.street}, ${siteConfig.address.city}` },
              { icon: Clock, label: t.hero.hours, value: `${t.hours.monSat} · 09:00–18:00` },
            ].map((c, i) => {
              const Ico = c.icon;
              const content = (
                <div className="bg-[#0A0A0C] p-5 h-full">
                  <Ico size={18} style={{ color: "var(--accent)" }} />
                  <p className="mt-3 text-[11px] uppercase tracking-wider text-zinc-500">{c.label}</p>
                  <p className="mt-1 text-sm text-white font-medium leading-snug">{c.value}</p>
                </div>
              );
              return c.href
                ? <a key={i} href={c.href} data-testid="hero-contact-phone" className="block hover:bg-white/5 transition-colors">{content}</a>
                : <div key={i}>{content}</div>;
            })}
          </motion.div>
        </div>
      </section>

      {/* QUICK SERVICES */}
      <section id="quick-services" className="py-24 md:py-32 bg-[var(--bg-primary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <Overline>{t.quickServices.overline}</Overline>
            <h2 className="mt-4 font-display font-semibold text-white text-4xl sm:text-5xl max-w-2xl">
              {t.quickServices.title}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {t.quickServices.items.map((s, i) => {
              const Ico = QS_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.05}>
                  <div data-testid={`quick-service-${i}`} className="group bg-[var(--bg-secondary)] p-8 md:p-10 h-full flex flex-col hover:bg-[var(--bg-tertiary)] transition-colors">
                    <Ico size={30} className="text-white transition-colors group-hover:text-[var(--accent)]" strokeWidth={1.4} />
                    <h3 className="mt-8 font-display text-2xl font-medium text-white">{s.name}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed flex-1">{s.desc}</p>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                      <Link to={pathFor(lang, "contact")} className="inline-flex items-center gap-2 text-sm font-medium text-white hover:gap-3 transition-all">
                        {t.cta.learnMore} <ArrowRight size={15} style={{ color: "var(--accent)" }} />
                      </Link>
                      <ServiceWhatsApp service={s.name} testid={`quick-service-wa-${i}`} />
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MECHANICAL PREVIEW */}
      <section className="py-24 md:py-32 bg-[var(--bg-secondary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>{t.mechPreview.overline}</Overline>
              <h2 className="mt-4 font-display font-semibold text-white text-4xl sm:text-5xl">{t.mechPreview.title}</h2>
              <p className="mt-6 text-zinc-400 leading-relaxed">{t.mechPreview.intro}</p>
              <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                {t.mechPreview.list.map((it, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check size={15} style={{ color: "var(--accent)" }} /> {it}
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <AccentButton to={pathFor(lang, "mechanical")} testid="home-mech-cta">{t.cta.seeMechanical}</AccentButton>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="relative overflow-hidden aspect-[4/3]">
                <img src={IMAGES.mechanic} alt={t.alts.mechanicPreview} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" style={{ objectPosition: "center 24%" }} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TIRES PREVIEW + MARQUEE */}
      <section className="py-24 md:py-32 bg-[var(--bg-primary)] overflow-hidden">
        <div className="relative select-none mb-16">
          <div className="marquee-track font-display text-6xl md:text-8xl font-bold uppercase text-stroke">
            <span className="px-6">{t.tiresPreview.marquee}&nbsp;</span>
            <span className="px-6">{t.tiresPreview.marquee}&nbsp;</span>
          </div>
        </div>
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <div className="relative overflow-hidden aspect-[4/3]">
                <img src={IMAGES.tireHands} alt={t.alts.tiresPreview} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal>
              <Overline>{t.tiresPreview.overline}</Overline>
              <h2 className="mt-4 font-display font-semibold text-white text-4xl sm:text-5xl">{t.tiresPreview.title}</h2>
              <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                {t.tiresPreview.items.map((it, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check size={15} style={{ color: "var(--accent)" }} /> {it}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <AccentButton to={pathFor(lang, "tires")} testid="home-tires-cta">{t.cta.seeTires}</AccentButton>
                <OutlineButton to={pathFor(lang, "contact")} testid="home-tires-offer">{t.cta.offer}</OutlineButton>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="mt-20 md:mt-28">
          <BrandMarquee />
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-[var(--bg-secondary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <Overline>{t.why.overline}</Overline>
            <h2 className="mt-4 font-display font-semibold text-white text-4xl sm:text-5xl">{t.why.title}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {t.why.items.map((w, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div data-testid={`why-${i}`} className="bg-[var(--bg-primary)] p-8 md:p-12 h-full flex gap-6">
                  <span className="font-display text-4xl font-bold" style={{ color: "var(--accent)" }}>{w.n}</span>
                  <div>
                    <h3 className="font-display text-2xl font-medium text-white">{w.name}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="on-dark relative py-24 md:py-32" style={{ background: "var(--accent)" }}>
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-semibold text-white text-4xl sm:text-6xl max-w-3xl mx-auto">{t.finalCta.title}</h2>
            <p className="mt-6 text-white/80 text-lg max-w-xl mx-auto">{t.finalCta.text}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={pathFor(lang, "contact")} data-testid="final-book-btn" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-[var(--accent)] bg-white px-8 py-4 hover:-translate-y-0.5 transition-transform">
                {t.cta.book} <ArrowRight size={16} />
              </Link>
              <a href={siteConfig.phoneHref} data-testid="final-call-btn" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white border border-white/50 hover:border-white px-8 py-4 transition-colors">
                <Phone size={16} /> {t.cta.call}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
